import type { JSONSchema7 } from 'json-schema';

import type { DeepReadonly } from './types';

/* eslint-disable @typescript-eslint/naming-convention */
// We want these to match the API schema as closely as possible

export const perplexityJsonSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    search_mode: {
      type: 'string',
      enum: ['academic', 'web'],
      default: 'web',
    },
    reasoning_effort: {
      type: 'string',
      enum: ['low', 'medium', 'high'],
      default: 'medium',
    },
    temperature: {
      type: 'number',
      description: 'Controls randomness in output generation. Values range from 0 to 2.',
      minimum: 0,
      maximum: 2,
      default: 0.2,
    },
    max_tokens: {
      type: 'integer',
      description: 'Maximum number of tokens to generate in the response.',
      minimum: 1,
      maximum: 4096,
      default: 1024,
    },
    top_p: {
      type: 'number',
      description: 'Nucleus sampling parameter. Controls diversity of output.',
      minimum: 0,
      maximum: 1,
      default: 0.9,
    },
    top_k: {
      type: 'integer',
      description: 'Limits token choices to the K most likely tokens.',
      minimum: 1,
      maximum: 100,
      default: 50,
    },
    presence_penalty: {
      type: 'number',
      description: 'Penalty for tokens that appear in the text so far.',
      minimum: -2,
      maximum: 2,
      default: 0,
    },
    frequency_penalty: {
      type: 'number',
      description: 'Penalty for tokens based on their frequency in the text so far.',
      minimum: -2,
      maximum: 2,
      default: 0,
    },
    search_domain_filter: {
      type: 'array',
      description: 'List of domains to focus the search on.',
      items: {
        type: 'string',
      },
    },
    search_recency_filter: {
      type: 'string',
      description: 'Filter search results by recency.',
      enum: ['month', 'week', 'day', 'hour'],
      default: 'month',
    },
    return_citations: {
      type: 'boolean',
      description: 'Whether to return citations in the response.',
      default: true,
    },
    return_images: {
      type: 'boolean',
      description: 'Whether to return images in the response.',
      default: false,
    },
    return_related_questions: {
      type: 'boolean',
      description: 'Whether to return related questions in the response.',
      default: false,
    },
    search_after_date_filter: {
      type: 'string',
      description: 'Filter search results by date.',
      format: 'date',
    },
    search_before_date_filter: {
      type: 'string',
      description: 'Filter search results by date.',
      format: 'date',
    },
    last_updated_after_filter: {
      type: 'string',
      description: 'Filter search results by date.',
      format: 'date',
    },
    last_updated_before_filter: {
      type: 'string',
      description: 'Filter search results by date.',
      format: 'date',
    },
    stream: {
      type: 'boolean',
      description: 'Whether to stream the response.',
      default: false,
    },
    response_format: {
      type: 'object',
    },
    web_search_options: {
      type: 'object',
    },
  },
  additionalProperties: false,
} as const;

// this confirms that the type is a valid JSON schema, but we want it as a `const` so we can use it to index the properties
export const perplexitySchemaProperties: DeepReadonly<JSONSchema7> = perplexityJsonSchema;

export const retrievePerplexityParameters = (): string[] => Object.keys(perplexityJsonSchema.properties);
